<template>
  <v-card class="dialog-card">
    <v-dialog v-model="overwritePresetDialog">
      <PresetOverrideDialogOuter
        :overwritePresetDialog="overwritePresetDialog"
        @close="
          overwritePresetDialog = null;
          overwriteExisting = false;
          send_to_upload_queue();
        "
        @override="
          overwritePresetDialog = null;
          overwriteExisting = true;
          send_to_upload_queue();
        " />
    </v-dialog>
    <v-dialog
      v-model="importFailed"
      max-width="1080px">
      <ChannelImportResult
        :results="importFailedResult"
        @close="importFailed = false" />
    </v-dialog>
    <v-dialog
      v-model="importConflicts"
      max-width="90vw"
      max-height="90vh">
      <ChannelConflictResolver
        :ch_uid="deluge.items[props.channel_did].uid"
        :queueOnly="true"
        :importFile="importFile"
        :conflictResults="importConflictsResult"
        @close="
          importConflicts = false;
          emit('close');
        " />
    </v-dialog>
    <ModalHeader
      helpFileName="AddQueue"
      icon="mdi-playlist-play"
      modalTitle="New Queue"
      @close="emit('close')" />
    <div class="px-4">
      <v-text-field
        autofocus
        filled
        label="Name"
        v-on:keyup.enter="NewQueue()"
        v-model="new_queue.name"></v-text-field>
      <v-textarea
        auto-grow
        filled
        v-on:keyup.enter="NewQueue()"
        label="Description"
        v-model="new_queue.description"></v-textarea>
    </div>
    <input
      accept=".cprq,.cprqa"
      type="file"
      style="display: none"
      multiple
      id="queue_file_upload"
      @input="send_to_upload_queue()" />
    <div class="modal-footer">
      <v-btn
        class="mr-3"
        color="primary"
        variant="flat"
        :loading="uploadingQueue"
        @click="open_file_dialog()">
        <v-icon class="mr-1">mdi-upload</v-icon>
        Upload
      </v-btn>
      <v-btn
        :disabled="!new_queue.name"
        color="primary"
        variant="flat"
        :loading="new_queue_loading"
        @click="NewQueue()">
        <v-icon class="mr-1">mdi-playlist-plus</v-icon>
        Create Queue
      </v-btn>
    </div>
  </v-card>
</template>
<script setup lang="ts">
import { defineProps, ref } from 'vue';
import { $http, uploadRawBodyWithProgress } from '../../stores/http.js';
import HelpButton from '../Help-Hints/HelpButton.vue';
import { deluge, state } from '@/stores/deluge.js';

import ModalHeader from '../Shared/Modals/ModalHeader.vue';
import ChannelImportResult from '../Channels/ChannelImportResult.vue';
import ChannelConflictResolver from '../Channels/ConflictComponents/ChannelConflictResolver.vue';

import { requireUserToSignOffUnknownReferences } from '../../components/Shared/referencesSignOff.ts';
import { isUserInputRequiredForPresetOveride } from '../../utils/handlePresetOverride.ts';

import PresetOverrideDialogOuter from '../Shared/PresetOverrideDialogOuter.vue';

const importFailed = ref(false);
const importFailedResult = ref({});

const importConflicts = ref(false);
const importConflictsResult = ref({});

const importFile = ref(null);

const overwriteExisting = ref(null);
const overwritePresetDialog = ref(false);

const uploadingQueue = ref(false);
function open_file_dialog() {
  const file_input = document.getElementById('queue_file_upload');
  file_input.click();
}

async function check_for_missing_assets(schema) {
  const url = `/check_import_schema_for_missing_files?ch_uid=${
    deluge.items[props.channel_did].uid
  }`;
  const response = await $http('POST', url, JSON.stringify(schema));
  const responseJSON = JSON.parse(response);

  return responseJSON;
}

async function check_for_conflicts(schema) {
  const url = `/check_import_schema_for_conflicts?ch_uid=${
    deluge.items[props.channel_did].uid
  }`;
  const response = await $http('POST', url, JSON.stringify(schema));
  const responseJSON = JSON.parse(response);

  return responseJSON;
}
function getResolvedConflictsCount(conflictResults) {
  if (!conflictResults) return 0;
  if (conflictResults.templates) {
    return Object.values(conflictResults.templates).reduce((acc, item) => {
      return (
        acc +
        Object.values(item.conflicts).filter((resolution) => !!resolution)
          .length
      );
    }, 0);
  }
  return 0;
}
async function importQueueArchive(file) {
  // Phase 1: Stream the raw .cprqa body to the prepare endpoint via XHR
  // (no FormData, no multipart) so the server can extract straight from
  // the network socket and we get upload progress events for the
  // BottomBar widget.
  const url = state.devRedirect + 'api/dynamic_engine/prepare_queue_archive';
  return await uploadRawBodyWithProgress(url, file);
}

async function send_to_upload_queue() {
  uploadingQueue.value = true;
  const file_input = document.getElementById('queue_file_upload');
  for (const file of file_input.files) {
    // .cprqa archive (data + media files) goes through prepare endpoint first;
    // .cprq is plain JSON and can be parsed client-side.
    const ext = file.name.split('.').pop().toLowerCase();
    const fileData =
      ext === 'cprqa'
        ? await importQueueArchive(file)
        : await new Response(file).json();

    const userInputRequired = await isUserInputRequiredForPresetOveride(
      fileData,
      deluge
    );

    if (userInputRequired && overwriteExisting.value === null) {
      overwritePresetDialog.value = userInputRequired;
      return;
    }

    fileData.ch = deluge.items[props.channel_did].uid;
    fileData.channel = null;

    const signedOff = await requireUserToSignOffUnknownReferences(fileData);

    if (!signedOff) {
      // User did not sign off on unknown references
      uploadingQueue.value = false;
      return;
    }

    const missingResponse = await check_for_missing_assets(fileData);

    if (missingResponse) {
      if (missingResponse.missing_assets || missingResponse.missing_templates) {
        importFailedResult.value = missingResponse;
        importFailed.value = true;
        return;
      }
    }

    const conflictResponse = await check_for_conflicts(fileData);

    if (conflictResponse) {
      if (
        getResolvedConflictsCount(conflictResponse) !== 0 ||
        conflictResponse.templates
      ) {
        importConflictsResult.value = conflictResponse;
        importConflicts.value = true;
        importFile.value = fileData;
        return;
      }
    }
    await $http(
      'POST',
      '/dynamic_engine/import_queue' +
        '?ch_uid=' +
        deluge.items[props.channel_did].uid +
        '&override_presets=' +
        Boolean(overwriteExisting.value),
      JSON.stringify(fileData)
    );
    uploadingQueue.value = false;
    emit('refresh', true);
    emit('close', true);
  }
  file_input.files = new DataTransfer().files;
  uploadingQueue.value = false;
}
const props = defineProps(['channel_did']);
const emit = defineEmits(['refresh', 'close']);
const new_queue_loading = ref(false);
const empty_queue = {
  name: '',
  description: '',
};
const new_queue = ref(empty_queue);
function CloseModal() {
  new_queue.value = JSON.parse(JSON.stringify(empty_queue));
  emit('refresh', true);
  emit('close', true);
}
function NewQueue() {
  if (new_queue.value.name) {
    new_queue.value_loading = true;
    $http(
      'POST',
      '/dynamic_engine/add_queue_to_channel?channel_did=' + props.channel_did,
      JSON.stringify({
        name: new_queue.value.name,
        description: new_queue.value.description,
      })
    )
      .then(() => {
        CloseModal();
        new_queue.value_loading = false;
      })
      .catch(() => {
        new_queue.value_loading = false;
      });
  }
}
</script>
